import { Table as ITable } from '@tanstack/react-table';
import { CSSProperties, ReactNode, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { Typography } from '../../ui';
import { BodyCell } from './bodyCell';
import { HeadCell } from './headCell';
import { header as headerVariant, row as rowVariant } from './variants';

interface Props<TData> {
  table: ITable<TData>;
  stickyHeader?: boolean;
  paginationSlot?: ReactNode;
  filterSlot?: ReactNode;
  className?: string;
}

export function Table<TData>(props: Props<TData>) {
  const id = useId();
  const {
    table,
    stickyHeader = false,
    className,
    paginationSlot,
    filterSlot,
  } = props;

  return (
    <div className="flex flex-col gap-y-24 w-full">
      {filterSlot}

      <div
        role="table"
        aria-rowcount={table.getRowModel().rows.length}
        className={twMerge(
          'col-span-full',
          'grid',
          'overflow-hidden',
          'border',
          'border-grayscale100',
          'grid-cols-[repeat(var(--column-count),_1fr)]',
          'rounded-8',
          !!paginationSlot && 'rounded-b-none',
          className,
        )}
        style={
          {
            '--column-count': table.getHeaderGroups()[0].headers.length,
          } as CSSProperties
        }
      >
        <div
          role="rowgroup"
          className={twMerge(
            headerVariant({ sticky: stickyHeader }),
            'grid grid-cols-subgrid col-span-full',
          )}
        >
          {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
            <div
              key={`table-${id}-headergroup-${headerGroupIndex}`}
              role="row"
              className="grid grid-cols-subgrid col-span-full"
            >
              {headerGroup.headers.map((column, headerIndex) => (
                <HeadCell
                  key={`table-${id}-headergroup-${headerGroupIndex}-${headerIndex}`}
                  header={column}
                  className="col-span-1"
                />
              ))}
            </div>
          ))}
        </div>
        {match(table.getRowCount() === 0)
          .with(false, () => (
            <div
              role="rowgroup"
              className="grid grid-cols-subgrid col-span-full"
            >
              {table.getRowModel().rows.map((row, rowIndex) => (
                <div
                  key={`table-${id}-row-${rowIndex}`}
                  role="row"
                  aria-rowindex={rowIndex}
                  className={twMerge(
                    rowVariant(),
                    'grid grid-cols-subgrid col-span-full',
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <BodyCell
                      key={`table-${id}-row-${rowIndex}-cell-${cellIndex}`}
                      cell={cell}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))
          .otherwise(() => (
            <div className="flex flex-col justify-center items-center p-64 col-span-full">
              <img
                src={`/images/illustrations/empty-table.svg`}
                alt={'Geen resultaten gevonden'}
                className="object-contain w-[190px] h-[155px]"
              />
              <Typography
                variant="body-l"
                weight="medium"
                className="text-grayscale600"
              >
                Geen resultaten gevonden
              </Typography>
            </div>
          ))}
      </div>
      {paginationSlot && <div className="-mt-24">{paginationSlot}</div>}
    </div>
  );
}
